/**
 * High-level execution APIs — executeTask / executeWorkflow
 *
 * Encapsulates the common boilerplate shared across all CLI consumers:
 *   1. Adapter creation
 *   2. DSL context loading (registries)
 *   3. Progress sink setup
 *   4. Task/workflow invocation with retry policy
 *
 * CLI consumers only need to provide:
 *   - domain-specific context (user_request)
 *   - adapter name + options
 *   - DSL blob (resolvedDsl from codegen)
 */

import { resolve } from "node:path";
import { type CreateAdapterOptions } from "./adapter-factory.js";
import {
  BoundResolver,
  getGuardrailRulesForAgent,
  type ArtifactBindingConfig,
  type ResolvedComponent,
} from "./bound-resolver.js";
import { loadDslContext, buildGuardrailRulesFromDsl } from "./dsl-context.js";
import { createGuardrailHooksFromRules } from "./guardrail-hooks.js";
import { createProgressSink, type ProgressSink } from "./progress-sink.js";
import { runTask, type TaskRunResult, type SdkAdapter, type ObserverEmitFn } from "./task-runner.js";
import { runWorkflow, type WorkflowResult, type WorkflowInvocation, type HandoffInput, type StepCompleteEvent, type StepResult, type WorkflowRegistries, type DelegateStep } from "./workflow-runner.js";
import type { RunToolchainChecksOptions } from "./toolchain-checker.js";
import type { TaskContext } from "./task-runner.js";
import type { ModelClass, ModelMappingConfig } from "./model-resolver.js";
import { createModelAwareAdapterSetup } from "./model-aware-adapter.js";
import { createAnalyzerBridge } from "./analyzer-bridge.js";
import { createRegistryFromSdkTools, type LibraryToolRegistry } from "./library-tool-binding.js";
import { buildNavigationContext, type NavigationContext } from "./navigation-context.js";

// ---------------------------------------------------------------------------
// Shared options
// ---------------------------------------------------------------------------

export interface ExecuteOptions {
  adapter: string;
  model?: string;
  dsl: Record<string, unknown>;
  projectDslPath?: string;
  logFile?: string;
  adapterOptions?: Partial<CreateAdapterOptions>;
  maxFollowUps?: number;
  maxRetries?: number;
  /** Pre-resolved component from BoundResolver (skips runtime resolve). */
  resolvedComponent?: ResolvedComponent;
  /** Component id for BoundResolver cache keying. */
  componentId?: string;
  /** Software binding paths for runtime guardrail resolution. */
  bindingPaths?: string[];
  /** Active guardrail policy name. */
  activeGuardrailPolicy?: string;
  /** Artifact binding config for runtime resolveBound(). */
  artifactBinding?: ArtifactBindingConfig;
  /** Path substitutions for artifact binding. */
  paths?: Record<string, string>;
  /**
   * Observer emit function for agent-axis spans (agent.session / agent.subagent /
   * toolchain.execution / evaluate.*). When undefined, all emission is a no-op.
   * Auto-wired from project.yaml `bindings.observability` by createProjectRuntime —
   * callers do NOT hand-write this.
   */
  observer?: ObserverEmitFn;
  /**
   * Optional toolchain-check observation config. When set together with `observer`,
   * the workflow runner runs these checks once post-run and emits one
   * toolchain.execution span per check (OBSERVATION ONLY — never changes the
   * workflow outcome). Auto-wired from project.yaml `toolchains` + `bindings.observability`
   * by createProjectRuntime; `observer`/`parentSpanId` are injected by runWorkflow.
   * Only consumed by executeWorkflow (toolchain checks are a run-level concern).
   */
  toolchain?: RunToolchainChecksOptions;
  /** Per-class adapter/model mapping from project.yaml (used by executeWorkflow / executeTask). */
  modelMapping?: ModelMappingConfig;
  /**
   * When true, `adapter` is used for all model classes; only the model name
   * varies per class (CLI `--adapter mock` override).
   */
  pinAdapter?: boolean;
}

// ---------------------------------------------------------------------------
// Shared context resolution
// ---------------------------------------------------------------------------

interface ExecutionContext {
  registries: WorkflowRegistries;
  dsl: Record<string, unknown>;
  resolvedComponent?: ResolvedComponent;
}

async function resolveExecutionContext(
  options: ExecuteOptions,
  requiredEntities?: { workflows?: string[]; tasks?: string[] },
): Promise<ExecutionContext> {
  const useBoundResolver =
    options.resolvedComponent ||
    (options.bindingPaths && options.bindingPaths.length > 0) ||
    options.artifactBinding;

  if (useBoundResolver) {
    // Merge the project-level DSL overlay (which may `extends: /$embedded/`)
    // over the embedded base BEFORE binding, so guardrail/artifact binding
    // operates on the fully-resolved DSL — not just the embedded base.
    let baseDsl = options.dsl;
    if (!options.resolvedComponent && options.projectDslPath) {
      const merged = await loadDslContext({
        embeddedDsl: options.dsl,
        projectDslPath: options.projectDslPath,
      });
      baseDsl = merged.dsl;
    }

    const component =
      options.resolvedComponent ??
      (await new BoundResolver().resolve({
        id: options.componentId ?? "default",
        embeddedDsl: baseDsl,
        bindingPaths: options.bindingPaths,
        activeGuardrailPolicy: options.activeGuardrailPolicy,
        artifactBinding: options.artifactBinding,
        paths: options.paths,
      }));

    if (requiredEntities) {
      const ctx = await loadDslContext({
        embeddedDsl: component.dsl,
        requiredEntities,
      });
      return { registries: ctx.registries, dsl: component.dsl, resolvedComponent: component };
    }

    return {
      registries: component.registries,
      dsl: component.dsl,
      resolvedComponent: component,
    };
  }

  const ctx = await loadDslContext({
    embeddedDsl: options.dsl,
    projectDslPath: options.projectDslPath,
    requiredEntities,
  });
  return { registries: ctx.registries, dsl: ctx.dsl };
}

function injectGuardrailHooks(
  adapterOptions: Partial<CreateAdapterOptions>,
  execCtx: ExecutionContext,
  targetAgentId?: string,
): void {
  if (adapterOptions.guardrailHooks) return;

  if (execCtx.resolvedComponent && targetAgentId) {
    const rules = getGuardrailRulesForAgent(
      execCtx.resolvedComponent,
      targetAgentId,
      execCtx.dsl,
    );
    if (
      rules &&
      (rules.commandRules.length > 0 ||
        rules.fileRules.length > 0 ||
        rules.contentRules.length > 0)
    ) {
      adapterOptions.guardrailHooks = createGuardrailHooksFromRules(rules);
    }
    return;
  }

  const rules = buildGuardrailRulesFromDsl(execCtx.dsl);
  if (
    rules &&
    (rules.commandRules.length > 0 ||
      rules.fileRules.length > 0 ||
      rules.contentRules.length > 0)
  ) {
    adapterOptions.guardrailHooks = createGuardrailHooksFromRules(rules);
  }
}

// ---------------------------------------------------------------------------
// Shared navigation context (Level 1) → analyzer bridge + task prompt
// ---------------------------------------------------------------------------

interface AnalyzerToolRegistryOptions {
  navigationIndex?: Record<string, unknown>;
  artifactPatterns?: Map<string, string[]>;
  projectRoot?: string;
}

function buildAnalyzerToolRegistry(
  options: AnalyzerToolRegistryOptions = {},
): LibraryToolRegistry {
  const bridge = createAnalyzerBridge({
    projectRoot: options.projectRoot ?? process.cwd(),
    navigationIndex: options.navigationIndex,
    artifactPatterns: options.artifactPatterns,
  });
  const tools = bridge.buildAnalyzerTools();
  return createRegistryFromSdkTools(tools);
}

/**
 * Derive the artifactId → path_patterns map the analyzer needs to scope its
 * AST/dependency graph build, from the Level 1 navigation index. Returns an
 * empty map when no usable artifacts are present.
 */
function deriveArtifactPatterns(
  navigationIndex?: Record<string, unknown>,
): Map<string, string[]> {
  const patterns = new Map<string, string[]>();
  const artifacts = navigationIndex?.artifacts as
    | Record<string, { files?: { path_patterns?: unknown } }>
    | undefined;
  if (!artifacts) return patterns;
  for (const [id, node] of Object.entries(artifacts)) {
    const pp = node?.files?.path_patterns;
    if (Array.isArray(pp) && pp.length > 0) {
      patterns.set(id, pp as string[]);
    }
  }
  return patterns;
}

/**
 * Build the Level 1 navigation context for an execution. Best-effort: when the
 * DSL is absent or navigation cannot be built (e.g. agent-contracts unavailable,
 * validation failure), execution degrades gracefully and proceeds without it
 * rather than throwing.
 */
async function buildNavigationContextSafely(
  execCtx: ExecutionContext,
): Promise<NavigationContext | undefined> {
  if (!execCtx.dsl) return undefined;
  try {
    // execCtx.dsl is already the fully merged + artifact-bound DSL, so we feed
    // it directly as the embedded DSL (no further overlay/binding required).
    return await buildNavigationContext({ embeddedDsl: execCtx.dsl });
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// executeTask
// ---------------------------------------------------------------------------

export interface TaskHookContext {
  adapter: SdkAdapter;
  registries: WorkflowRegistries;
  taskId: string;
}

export interface ExecuteTaskOptions extends ExecuteOptions {
  request: string;
  hooks?: {
    beforeTask?: (ctx: TaskHookContext) => Promise<void>;
    afterTask?: (result: TaskRunResult) => Promise<void>;
  };
}

export async function executeTask(
  taskId: string,
  options: ExecuteTaskOptions,
): Promise<TaskRunResult> {
  const execCtx = await resolveExecutionContext(options, { tasks: [taskId] });
  const taskDef = execCtx.registries.taskRegistry?.[taskId];
  const targetAgentId = taskDef?.target_agent;
  const modelClass = taskDef?.model_class as ModelClass | undefined;

  const adapterOptions = { ...options.adapterOptions };
  injectGuardrailHooks(adapterOptions, execCtx, targetAgentId);

  const { resolveAdapter: resolveTaskAdapter, modelResolver } = await createModelAwareAdapterSetup({
    fallbackAdapter: options.adapter,
    fallbackModel: options.model,
    modelMapping: options.modelMapping,
    adapterOptions,
    pinAdapter: options.pinAdapter,
  });
  const resolved = modelResolver.resolve(modelClass);
  const adapter = await resolveTaskAdapter(modelClass);

  const progressSink = createProgressSink(
    options.logFile
      ? { stderr: true, file: resolve(options.logFile), naming: "single" }
      : { stderr: true },
  );

  const navContext = await buildNavigationContextSafely(execCtx);
  const analyzerTools = buildAnalyzerToolRegistry({
    navigationIndex: navContext?.navigationIndex,
    artifactPatterns: deriveArtifactPatterns(navContext?.navigationIndex),
  });

  const taskContext: TaskContext = { user_request: options.request };
  if (navContext?.navigationIndex) {
    taskContext.variables = { navigationIndex: navContext.navigationIndex };
  }

  try {
    if (options.hooks?.beforeTask) {
      await options.hooks.beforeTask({ adapter, registries: execCtx.registries, taskId });
    }

    const result = await runTask(adapter, taskId, taskContext, {
      maxFollowUps: options.maxFollowUps ?? 2,
      maxRetries: options.maxRetries ?? 0,
      progressOutput: progressSink,
      observer: options.observer,
      libraryTools: analyzerTools,
      envelopeAdapter: resolved.adapter,
      envelopeModel: resolved.model,
      ...execCtx.registries,
    });

    if (options.hooks?.afterTask) {
      await options.hooks.afterTask(result);
    }

    return result;
  } finally {
    progressSink.close();
  }
}

// ---------------------------------------------------------------------------
// executeWorkflow
// ---------------------------------------------------------------------------

export interface WorkflowHookContext {
  adapter: SdkAdapter;
  registries: WorkflowRegistries;
  workflowId: string;
}

export interface ExecuteWorkflowOptions extends ExecuteOptions {
  request?: string;
  handoff?: HandoffInput<string>;
  context?: {
    cwd?: string;
    environment?: "local" | "ci" | "remote";
    artifacts?: Record<string, string>;
    variables?: Record<string, unknown>;
  };
  autoApproveGates?: boolean;
  hooks?: {
    beforeWorkflow?: (ctx: WorkflowHookContext) => Promise<void>;
    afterWorkflow?: (result: WorkflowResult) => Promise<void>;
    onStepComplete?: (event: StepCompleteEvent) => void;
    onGate?: (gateKind: string, description: string, stepResults: StepResult[]) => Promise<boolean>;
    onOptionalStep?: (step: DelegateStep, context: TaskContext) => Promise<boolean>;
  };
}

export async function executeWorkflow(
  workflowId: string,
  options: ExecuteWorkflowOptions,
): Promise<WorkflowResult> {
  const execCtx = await resolveExecutionContext(options, { workflows: [workflowId] });

  const adapterOptions = { ...options.adapterOptions };
  // Workflow entry agent is resolved per-step; use embedded rules as fallback.
  injectGuardrailHooks(adapterOptions, execCtx);

  const { factory: adapterOrFactory, defaultAdapter: adapter, modelResolver } =
    await createModelAwareAdapterSetup({
      fallbackAdapter: options.adapter,
      fallbackModel: options.model,
      modelMapping: options.modelMapping,
      adapterOptions,
      pinAdapter: options.pinAdapter,
    });

  const progressSink = createProgressSink(
    options.logFile
      ? { stderr: true, file: resolve(options.logFile), naming: "single" }
      : { stderr: true },
  );

  const navContext = await buildNavigationContextSafely(execCtx);
  const analyzerTools = buildAnalyzerToolRegistry({
    navigationIndex: navContext?.navigationIndex,
    artifactPatterns: deriveArtifactPatterns(navContext?.navigationIndex),
  });

  // Propagate the navigation index to every step's TaskContext.variables via
  // the workflow invocation context (workflow-runner spreads context.variables
  // onto each delegate/evaluate step).
  const workflowContext = navContext?.navigationIndex
    ? {
        ...options.context,
        variables: {
          ...options.context?.variables,
          navigationIndex: navContext.navigationIndex,
        },
      }
    : options.context;

  try {
    if (options.hooks?.beforeWorkflow) {
      await options.hooks.beforeWorkflow({ adapter, registries: execCtx.registries, workflowId });
    }

    const invocation: WorkflowInvocation = {
      workflow: workflowId,
      user_request: options.request,
      handoff: options.handoff,
      progressOutput: progressSink,
      observer: options.observer,
      toolchain: options.toolchain,
      libraryTools: analyzerTools,
      resolveModelMeta: (modelClass) => modelResolver.resolve(modelClass),
      runtime: {
        maxFollowUps: options.maxFollowUps ?? 2,
        maxRetries: options.maxRetries ?? 0,
      },
      hooks: {
        onStepComplete: options.hooks?.onStepComplete,
        onGate: options.hooks?.onGate ?? (options.autoApproveGates ? async () => true : undefined),
        onOptionalStep: options.hooks?.onOptionalStep,
      },
      context: workflowContext,
    };

    const result = await runWorkflow(adapterOrFactory, invocation, execCtx.registries);

    if (options.hooks?.afterWorkflow) {
      await options.hooks.afterWorkflow(result);
    }

    return result;
  } finally {
    progressSink.close();
  }
}
